import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, FileCheck, Users, Phone, CheckCircle, ArrowRight, BookOpen, Calendar, Clock, MapPin, Tag, ChevronLeft, ChevronRight, Upload, X, File, Plus, Trash2, Shirt, ShoppingCart, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Business Hours
const CPR_HOURS = {
  0: null, // Sunday - by appointment
  1: { start: 9, end: 19 }, // Monday 9AM-7PM
  2: { start: 9, end: 19 },
  3: { start: 9, end: 19 },
  4: { start: 9, end: 19 },
  5: { start: 9, end: 19 }, // Friday
  6: { start: 9, end: 17 }, // Saturday 9AM-5PM
};

const NOTARY_HOURS = {
  0: null, // Sunday - by appointment
  1: { start: 9, end: 18 }, // Monday 9AM-6PM
  2: { start: 9, end: 18 },
  3: { start: 9, end: 18 },
  4: { start: 9, end: 18 },
  5: { start: 9, end: 18 }, // Friday
  6: { start: 10, end: 16 }, // Saturday 10AM-4PM
};

// Course Data
const oneOnOneCourses = [
  { name: 'Heartsaver CPR/AED', price: '$77' },
  { name: 'Heartsaver First Aid', price: '$77' },
  { name: 'CPR/AED & First Aid', price: '$115' },
  { name: 'BLS for Healthcare Providers', price: '$85' },
  { name: 'BLS AND First Aid', price: '$135' },
  { name: 'Family & Friends CPR', price: '$10' },
];

const blendedCourses = [
  { name: 'Heartsaver CPR/AED', price: '$75', promo: '$60' },
  { name: 'Heartsaver First Aid', price: '$70', promo: '$55' },
  { name: 'CPR/AED & First Aid', price: '$100', promo: '$85' },
  { name: 'BLS for Healthcare Providers', price: '$80', promo: '$65' },
  { name: 'BLS AND First Aid', price: '$110', promo: '$95' },
  { name: 'Blood Borne Pathogens', price: '$40', promo: '$25' },
  { name: 'K-12 CPR/AED & First Aid (Student Deal)', price: '$40', promo: '$40', studentDeal: true },
  { name: 'Family & Friends CPR', price: '$10', promo: '$10' },
];

const groupCourses = [
  { name: 'Heartsaver CPR/AED', price: '$70/person' },
  { name: 'Heartsaver First Aid', price: '$70/person' },
  { name: 'CPR/AED & First Aid', price: '$100/person' },
  { name: 'BLS for Healthcare Providers', price: '$80/person' },
  { name: 'BLS AND First Aid', price: '$110/person' },
  { name: 'Blood Borne Pathogens', price: '$40/person' },
  { name: 'K-12 CPR/AED & First Aid (Student Deal)', price: '$40/person', studentDeal: true },
  { name: 'Family & Friends CPR', price: '$10/person' },
];

const skillsTestCourses = [
  { name: 'CPR / First Aid Skills Check', price: '$60' },
  { name: 'BLS Skills Check', price: '$55' },
];

const locationTypes = ['Your Location (We Come to You)'];

function getCourses(trainingType) {
  switch(trainingType) {
    case 'one-on-one': return oneOnOneCourses;
    case 'blended': return blendedCourses;
    case 'group': return groupCourses;
    case 'skills-test': return skillsTestCourses;
    default: return [];
  }
}

function getTrainingLabel(type) {
  switch(type) {
    case 'one-on-one': return 'One-on-One Training';
    case 'blended': return 'Blended Courses';
    case 'group': return 'Group Classes (Min 6)';
    case 'skills-test': return 'Skills Testing Only';
    default: return '';
  }
}

// Check if promo is active (expires 2/28/2026)
const isPromoActive = () => {
  const now = new Date();
  const promoEnd = new Date('2026-02-28T23:59:59');
  return now <= promoEnd;
};

// Generate time slots for a given date and service type
function getTimeSlots(date, serviceType) {
  const hours = serviceType === 'notary' ? NOTARY_HOURS : CPR_HOURS;
  const dayOfWeek = date.getDay();
  const dayHours = hours[dayOfWeek];
  
  if (!dayHours) return []; // Closed
  
  const slots = [];
  for (let hour = dayHours.start; hour < dayHours.end; hour++) {
    slots.push(`${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`);
    if (hour + 0.5 < dayHours.end) {
      slots.push(`${hour > 12 ? hour - 12 : hour}:30 ${hour >= 12 ? 'PM' : 'AM'}`);
    }
  }
  return slots;
}

// Calendar Component
function BookingCalendar({ selectedDate, onDateSelect, serviceType }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const hours = serviceType === 'notary' ? NOTARY_HOURS : CPR_HOURS;
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before the first day of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    
    const dayHours = hours[date.getDay()];
    return dayHours !== null;
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="bg-white rounded-xl border p-4">
      <div className="flex justify-between items-center mb-4">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-2 hover:bg-gray-100 rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="font-semibold text-[#2a2873]">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-2 hover:bg-gray-100 rounded-lg">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="font-medium text-[#5a2b8a] py-2">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, idx) => {
          if (!date) return <div key={idx} className="p-2" />;
          const available = isDateAvailable(date);
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
          const isToday = date.toDateString() === new Date().toDateString();
          
          return (
            <button
              key={idx}
              onClick={() => available && onDateSelect(date)}
              disabled={!available}
              className={`p-2 rounded-lg text-sm transition-all ${
                isSelected ? 'bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white' :
                isToday ? 'bg-[#5a2b8a]/10 text-[#5a2b8a] font-bold' :
                available ? 'hover:bg-[#c31c31]/10 text-[#2a2873]' :
                'text-gray-300 cursor-not-allowed'
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex gap-4 text-xs text-[#5a2b8a]">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-gradient-to-r from-[#c31c31] to-[#5a2b8a]" /> Selected</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-[#5a2b8a]/10" /> Today</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-gray-100" /> Unavailable</div>
      </div>
    </div>
  );
}

export default function BookNow() {
  const [cprForm, setCprForm] = useState({
    name: '', email: '', phone: '', trainingType: '', courseType: '',
    selectedDate: null, selectedTime: '', numberOfStudents: '1', locationType: '', address: '', notes: '',
  });
  const [notaryForm, setNotaryForm] = useState({
    name: '', email: '', phone: '', address: '', numberOfSigners: '1',
    numberOfDocuments: '1', selectedDate: null, selectedTime: '', documentType: '', notes: '',
  });
  const [jaxNotaryForm, setJaxNotaryForm] = useState({
    name: '', email: '', phone: '', address: '', numberOfSigners: '1',
    numberOfDocuments: '1', selectedDate: null, selectedTime: '', documentType: '', notes: '',
  });
  const [isSubmittingCpr, setIsSubmittingCpr] = useState(false);
  const [isSubmittingNotary, setIsSubmittingNotary] = useState(false);
  const [isSubmittingJaxNotary, setIsSubmittingJaxNotary] = useState(false);
  const [showBlendedInstructions, setShowBlendedInstructions] = useState(false);
  const [showK12ShirtRecommendation, setShowK12ShirtRecommendation] = useState(false);
  const [k12ChildCount, setK12ChildCount] = useState(0);
  
  // K-12 Group specific state
  const [k12Children, setK12Children] = useState([{ name: '' }]);
  const [parentName, setParentName] = useState('');
  
  // Skills Test multiple students state
  const [skillsTestStudents, setSkillsTestStudents] = useState([{ name: '', phone: '', email: '' }]);
  const isSkillsTest = cprForm.trainingType === 'skills-test';
  
  // File upload states
  const [cprFile, setCprFile] = useState(null);
  const [notaryFile, setNotaryFile] = useState(null);
  const [uploadingCpr, setUploadingCpr] = useState(false);
  const [uploadingNotary, setUploadingNotary] = useState(false);
  const cprFileInputRef = useRef(null);
  const notaryFileInputRef = useRef(null);
  
  // Payment states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [paymentType, setPaymentType] = useState(''); // 'cpr', 'notary', 'jax-notary'

  const selectedCourses = getCourses(cprForm.trainingType);
  const selectedCourse = selectedCourses.find(c => c.name === cprForm.courseType);
  const showPromo = isPromoActive() && cprForm.trainingType === 'blended';
  
  // Check if K-12 Group course is selected
  const isK12GroupCourse = cprForm.trainingType === 'group' && cprForm.courseType?.includes('K-12');

  const cprTimeSlots = cprForm.selectedDate ? getTimeSlots(cprForm.selectedDate, 'cpr') : [];
  const notaryTimeSlots = notaryForm.selectedDate ? getTimeSlots(notaryForm.selectedDate, 'notary') : [];
  const jaxNotaryTimeSlots = jaxNotaryForm.selectedDate ? getTimeSlots(jaxNotaryForm.selectedDate, 'notary') : [];

  // K-12 Children handlers
  const addChild = () => {
    setK12Children([...k12Children, { name: '' }]);
  };
  
  const removeChild = (index) => {
    if (k12Children.length > 1) {
      setK12Children(k12Children.filter((_, i) => i !== index));
    }
  };
  
  const updateChildName = (index, name) => {
    const updated = [...k12Children];
    updated[index] = { name };
    setK12Children(updated);
  };

  // Helper to extract numeric price from string like "$77" or "$70/person"
  const extractPrice = (priceStr, numberOfStudents = 1) => {
    if (!priceStr) return 0;
    const match = priceStr.match(/\$(\d+)/);
    if (!match) return 0;
    const basePrice = parseFloat(match[1]);
    // For group courses (per person), multiply by students
    if (priceStr.includes('/person')) {
      return basePrice * parseInt(numberOfStudents);
    }
    return basePrice;
  };

  // Create Square payment link
  const createPaymentLink = async (bookingData) => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-link`, bookingData);
      return response.data;
    } catch (error) {
      console.error('Payment link creation failed:', error);
      toast.error('Could not create payment link. You can pay later or call us.');
      return null;
    } finally {
      setPaymentLoading(false);
    }
  };

  // Handle payment for booking
  const handlePayNow = async () => {
    if (!pendingBooking) return;
    
    const paymentData = await createPaymentLink(pendingBooking);
    if (paymentData?.payment_link_url) {
      // Open Square checkout in new tab
      window.open(paymentData.payment_link_url, '_blank');
      toast.success('Redirecting to secure payment page...');
      setShowPaymentModal(false);
      setPendingBooking(null);
    }
  };

  // Skip payment (pay later)
  const handlePayLater = () => {
    toast.info('No problem! You can pay when we contact you to confirm your appointment.');
    setShowPaymentModal(false);
    setPendingBooking(null);
  };

  // Skills Test student management
  const addSkillsTestStudent = () => {
    setSkillsTestStudents([...skillsTestStudents, { name: '', phone: '', email: '' }]);
  };

  const removeSkillsTestStudent = (index) => {
    if (skillsTestStudents.length > 1) {
      setSkillsTestStudents(skillsTestStudents.filter((_, i) => i !== index));
    }
  };

  const updateSkillsTestStudent = (index, field, value) => {
    const updated = [...skillsTestStudents];
    updated[index][field] = value;
    setSkillsTestStudents(updated);
  };

  // File upload handler
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.path;
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('File upload failed. Please try again.');
      return null;
    }
  };

  const handleCprFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setCprFile(file);
    }
  };

  const handleNotaryFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      setNotaryFile(file);
    }
  };

  const handleCprSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCpr(true);
    
    const price = showPromo && selectedCourse?.promo ? selectedCourse.promo : selectedCourse?.price;
    const fullCourseType = `${getTrainingLabel(cprForm.trainingType)} - ${cprForm.courseType} (${price})${showPromo ? ' [PROMO $15 OFF]' : ''}`;
    const preferredDate = cprForm.selectedDate ? cprForm.selectedDate.toISOString().split('T')[0] : '';
    
    // Build K-12 specific notes
    let k12Notes = '';
    if (isK12GroupCourse) {
      const childrenNames = k12Children.map(c => c.name).filter(n => n.trim()).join(', ');
      k12Notes = `[K-12 REGISTRATION]\nParent/Guardian: ${parentName}\nChild(ren): ${childrenNames}\nTotal Children: ${k12Children.length}\n\n`;
    }
    
    // Build Skills Test students notes
    let skillsTestNotes = '';
    if (isSkillsTest && skillsTestStudents.length > 0) {
      const studentDetails = skillsTestStudents.map((s, i) => 
        `Student ${i + 1}: ${s.name} | Phone: ${s.phone} | Email: ${s.email}`
      ).join('\n');
      skillsTestNotes = `[SKILLS TEST REGISTRATION - ${skillsTestStudents.length} STUDENTS]\n${studentDetails}\n\n`;
    }
    
    try {
      // Upload file if present
      let documentPath = '';
      if (cprFile) {
        setUploadingCpr(true);
        documentPath = await uploadFile(cprFile);
        setUploadingCpr(false);
      }
      
      const bookingResponse = await axios.post(`${API}/bookings/cpr`, { 
        ...cprForm, 
        name: isK12GroupCourse ? parentName : (isSkillsTest ? skillsTestStudents[0]?.name : cprForm.name),
        email: isSkillsTest ? skillsTestStudents[0]?.email : cprForm.email,
        phone: isSkillsTest ? skillsTestStudents[0]?.phone : cprForm.phone,
        courseType: fullCourseType,
        preferredDate,
        preferredTime: cprForm.selectedTime,
        numberOfStudents: isK12GroupCourse ? String(k12Children.length) : (isSkillsTest ? String(skillsTestStudents.length) : cprForm.numberOfStudents),
        notes: skillsTestNotes + k12Notes + (cprForm.notes || ''),
        documentPath: documentPath || '',
        students: isSkillsTest ? skillsTestStudents.filter(s => s.name && s.phone && s.email) : null,
      });
      
      toast.success('Booking confirmed!', { description: 'Check your email for confirmation details.' });
      if (cprForm.trainingType === 'blended') setShowBlendedInstructions(true);
      
      // Calculate total price for payment
      const numStudents = isK12GroupCourse ? k12Children.length : (isSkillsTest ? skillsTestStudents.length : parseInt(cprForm.numberOfStudents) || 1);
      const totalAmount = extractPrice(price, numStudents);
      
      // Prepare payment data
      if (totalAmount > 0) {
        const customerName = isK12GroupCourse ? parentName : (isSkillsTest ? skillsTestStudents[0]?.name : cprForm.name);
        setPendingBooking({
          booking_id: bookingResponse.data?.id || `cpr-${Date.now()}`,
          booking_type: 'cpr',
          amount: totalAmount,
          description: `${cprForm.courseType} - ${getTrainingLabel(cprForm.trainingType)}`,
          customer_name: customerName,
          customer_email: isSkillsTest ? skillsTestStudents[0]?.email : cprForm.email,
        });
        setPaymentType('cpr');
        setShowPaymentModal(true);
      }
      
      // Show shirt recommendation for K-12 registrations
      if (isK12GroupCourse) {
        setK12ChildCount(k12Children.length);
        setShowK12ShirtRecommendation(true);
      } else {
        // Show merch recommendation for all other bookings too
        setShowK12ShirtRecommendation(true);
      }
      
      setCprForm({ name: '', email: '', phone: '', trainingType: '', courseType: '', selectedDate: null, selectedTime: '', numberOfStudents: '1', locationType: '', address: '', notes: '' });
      setCprFile(null);
      // Reset K-12 fields
      setK12Children([{ name: '' }]);
      setParentName('');
      // Reset Skills Test fields
      setSkillsTestStudents([{ name: '', phone: '', email: '' }]);
    } catch (error) {
      toast.error('Failed to submit. Please call us.');
    }
    setIsSubmittingCpr(false);
  };

  const handleNotarySubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingNotary(true);
    const preferredDate = notaryForm.selectedDate ? notaryForm.selectedDate.toISOString().split('T')[0] : '';
    
    try {
      // Upload file if present
      let documentPath = '';
      if (notaryFile) {
        setUploadingNotary(true);
        documentPath = await uploadFile(notaryFile);
        setUploadingNotary(false);
      }
      
      await axios.post(`${API}/bookings/notary`, {
        ...notaryForm,
        preferredDate,
        preferredTime: notaryForm.selectedTime,
        documentPath: documentPath || '',
      });
      toast.success('Notary appointment confirmed!', { description: 'Check your email for confirmation.' });
      setNotaryForm({ name: '', email: '', phone: '', address: '', numberOfSigners: '1', numberOfDocuments: '1', selectedDate: null, selectedTime: '', documentType: '', notes: '' });
      setNotaryFile(null);
      // Show merch recommendation
      setShowK12ShirtRecommendation(true);
    } catch (error) {
      toast.error('Failed to submit. Please call us.');
    }
    setIsSubmittingNotary(false);
  };

  // Jacksonville Notary Submit
  const handleJaxNotarySubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingJaxNotary(true);
    const preferredDate = jaxNotaryForm.selectedDate ? jaxNotaryForm.selectedDate.toISOString().split('T')[0] : '';
    
    try {
      await axios.post(`${API}/bookings/notary`, {
        ...jaxNotaryForm,
        preferredDate,
        preferredTime: jaxNotaryForm.selectedTime,
        documentPath: '',
        notes: `[JACKSONVILLE, FL - Shiffon Champion-Holmes] ${jaxNotaryForm.notes}`,
      });
      toast.success('Jacksonville Notary appointment confirmed!', { description: 'Shiffon will contact you shortly.' });
      setJaxNotaryForm({ name: '', email: '', phone: '', address: '', numberOfSigners: '1', numberOfDocuments: '1', selectedDate: null, selectedTime: '', documentType: '', notes: '' });
      // Show merch recommendation
      setShowK12ShirtRecommendation(true);
    } catch (error) {
      toast.error('Failed to submit. Please call 904-465-5065.');
    }
    setIsSubmittingJaxNotary(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Promo Banner */}
      {isPromoActive() && (
        <div className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Tag className="w-5 h-5" />
            <span className="font-bold">LIMITED TIME: $15 OFF All Blended Courses!</span>
            <Badge variant="secondary" className="bg-white/20 text-white">Expires 2/28/2026</Badge>
          </div>
        </div>
      )}

      <section className="bg-gradient-to-br from-red-50 via-white to-purple-50 py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#2a2873] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Book Your Appointment</h1>
          <p className="text-lg text-[#5a2b8a]/70 mb-2">Select your service area and book online</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Blended Instructions */}
          {showBlendedInstructions && (
            <Alert className="mb-8 bg-[#5a2b8a]/10 border-[#5a2b8a]">
              <BookOpen className="h-5 w-5 text-[#5a2b8a]" />
              <AlertTitle className="text-[#2a2873] font-semibold">Blended Course Instructions</AlertTitle>
              <AlertDescription className="text-[#5a2b8a]/80 mt-2">
                <ol className="list-decimal list-inside space-y-1">
                  <li>Login: <a href="https://members.cprtrainingschool.com/my-account/" target="_blank" rel="noopener noreferrer" className="text-[#c31c31] underline">members.cprtrainingschool.com/my-account/</a></li>
                  <li>Go to <strong>Cards/Keys/Ebooks TAB</strong></li>
                  <li>Input your code from your email</li>
                </ol>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowBlendedInstructions(false)}>Close</Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Merch Recommendation Popup */}
          {showK12ShirtRecommendation && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8"
            >
              <Alert className="bg-gradient-to-r from-[#c31c31]/10 to-[#5a2b8a]/10 border-[#c31c31]">
                <Shirt className="h-6 w-6 text-[#c31c31]" />
                <AlertTitle className="text-[#2a2873] font-semibold text-lg">
                  🎉 Booking Complete! Would You Like Some LifeBreath Merch?
                </AlertTitle>
                <AlertDescription className="text-[#5a2b8a]/80 mt-3">
                  <p className="mb-3">
                    Show your support with our exclusive merch - T-shirts & Specialty Cups!
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white/50 rounded-lg p-3 text-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_lifebreath/artifacts/wszpnt2r_ChatGPT%20Image%20Mar%204%2C%202026%2C%2005_46_53%20PM.png"
                        alt="CPR T-Shirts"
                        className="w-24 h-24 object-cover rounded-lg shadow-md mx-auto mb-2"
                      />
                      <p className="font-bold text-[#c31c31]">T-Shirts $20-$30</p>
                      <p className="text-xs">Kids & Adults</p>
                    </div>
                    <div className="bg-white/50 rounded-lg p-3 text-center">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG"
                        alt="Specialty Cups"
                        className="w-24 h-24 object-cover rounded-lg shadow-md mx-auto mb-2"
                      />
                      <p className="font-bold text-orange-500">Specialty Cups $25</p>
                      <p className="text-xs">5 Designs</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/shop">
                      <Button className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] hover:from-[#a51828] hover:to-[#4a2370] rounded-full">
                        <ShoppingCart className="w-4 h-4 mr-2" /> Shop Now
                      </Button>
                    </Link>
                    <Button variant="outline" onClick={() => setShowK12ShirtRecommendation(false)}>
                      Maybe Later
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </motion.div>
          )}

          {/* Main Service Area Tabs */}
          <Tabs defaultValue="statesboro" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 h-16">
              <TabsTrigger value="statesboro" className="flex flex-col data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#c31c31] data-[state=active]:to-[#5a2b8a] data-[state=active]:text-white rounded-l-full h-full" data-testid="statesboro-tab">
                <span className="font-bold">Statesboro, GA</span>
                <span className="text-xs opacity-80">Hope N. • 912-388-6305</span>
              </TabsTrigger>
              <TabsTrigger value="jacksonville" className="flex flex-col data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#4f62c8] data-[state=active]:to-[#2a2873] data-[state=active]:text-white rounded-r-full h-full" data-testid="jacksonville-tab">
                <span className="font-bold">Jacksonville, FL</span>
                <span className="text-xs opacity-80">Shiffon C-H • 904-465-5065</span>
              </TabsTrigger>
            </TabsList>

            {/* STATESBORO, GA - CPR & Notary */}
            <TabsContent value="statesboro">
              <Card className="border-0 shadow-xl mb-4">
                <CardHeader className="bg-gradient-to-r from-[#c31c31]/10 to-[#5a2b8a]/10 border-b">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-[#c31c31]" />
                    <div>
                      <CardTitle className="text-[#2a2873]">Statesboro, Georgia & Surrounding Areas</CardTitle>
                      <CardDescription>CPR Training & Mobile Notary Services • <a href="tel:912-388-6305" className="text-[#c31c31] hover:underline">912-388-6305</a></CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Tabs defaultValue="cpr" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 h-14">
                  <TabsTrigger value="cpr" className="flex items-center gap-2 data-[state=active]:bg-[#c31c31] data-[state=active]:text-white rounded-l-full h-full" data-testid="cpr-tab">
                    <Heart className="w-5 h-5" /> CPR & First Aid
                  </TabsTrigger>
                  <TabsTrigger value="notary-ga" className="flex items-center gap-2 data-[state=active]:bg-[#4f62c8] data-[state=active]:text-white rounded-r-full h-full" data-testid="notary-ga-tab">
                    <FileCheck className="w-5 h-5" /> Mobile Notary
                  </TabsTrigger>
                </TabsList>

            {/* CPR Booking */}
            <TabsContent value="cpr">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#c31c31]/5 to-[#5a2b8a]/5">
                  <CardTitle className="text-2xl text-[#2a2873]">CPR/First Aid Training</CardTitle>
                  <CardDescription>Select training type, course, date & time</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleCprSubmit} className="space-y-6">
                    {/* Step 1: Training Type */}
                    <div className="space-y-3">
                      <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#c31c31] text-white text-sm flex items-center justify-center">1</span> Training Type</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { key: 'one-on-one', icon: Heart, label: 'One-on-One', desc: 'Personal instruction', color: '#c31c31' },
                          { key: 'blended', icon: BookOpen, label: 'Blended', desc: '$15 OFF!', color: '#5a2b8a', badge: true },
                          { key: 'group', icon: Users, label: 'Group', desc: 'Min 6 | 2-3 hrs', color: '#4f62c8' },
                          { key: 'skills-test', icon: CheckCircle, label: 'Skills Test', desc: 'Online done', color: '#c31c31' },
                        ].map(t => (
                          <button key={t.key} type="button" onClick={() => setCprForm({...cprForm, trainingType: t.key, courseType: '', selectedDate: null, selectedTime: ''})} 
                            className={`p-4 rounded-xl border-2 text-left transition-all relative ${cprForm.trainingType === t.key ? `border-[${t.color}] bg-[${t.color}]/10` : 'border-slate-200 hover:border-[#5a2b8a]'}`}>
                            {t.badge && isPromoActive() && <Badge className="absolute -top-2 -right-2 bg-[#c31c31] text-white text-xs">$15 OFF</Badge>}
                            <t.icon className="w-5 h-5 mb-2" style={{ color: t.color }} />
                            <p className="font-semibold text-sm text-[#2a2873]">{t.label}</p>
                            <p className="text-xs text-slate-500">{t.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Step 2: Course */}
                    {cprForm.trainingType && (
                      <div className="space-y-3">
                        <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#5a2b8a] text-white text-sm flex items-center justify-center">2</span> Select Course</Label>
                        <div className="grid gap-2">
                          {selectedCourses.map((course, idx) => (
                            <button key={idx} type="button" onClick={() => setCprForm({...cprForm, courseType: course.name})} 
                              className={`p-4 rounded-lg border-2 flex justify-between items-center ${cprForm.courseType === course.name ? 'border-[#c31c31] bg-red-50' : 'border-slate-200 hover:border-[#5a2b8a]'}`}>
                              <span className={`font-medium ${cprForm.courseType === course.name ? 'text-[#c31c31]' : 'text-[#2a2873]'}`}>{course.name}</span>
                              <div className="text-right">
                                {showPromo && course.promo ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-400 line-through text-sm">{course.price}</span>
                                    <span className="font-bold text-lg text-[#c31c31]">{course.promo}</span>
                                  </div>
                                ) : (
                                  <span className="font-bold text-lg text-[#5a2b8a]">{course.price}</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Step 3: Date & Time */}
                    {cprForm.courseType && (
                      <div className="space-y-3">
                        <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#4f62c8] text-white text-sm flex items-center justify-center">3</span> Select Date & Time</Label>
                        <div className="grid md:grid-cols-2 gap-4">
                          <BookingCalendar selectedDate={cprForm.selectedDate} onDateSelect={(date) => setCprForm({...cprForm, selectedDate: date, selectedTime: ''})} serviceType="cpr" />
                          {cprForm.selectedDate && (
                            <div className="bg-[#5a2b8a]/5 rounded-xl p-4">
                              <p className="font-medium text-[#2a2873] mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Available Times</p>
                              {cprTimeSlots.length > 0 ? (
                                <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                                  {cprTimeSlots.map(slot => (
                                    <button key={slot} type="button" onClick={() => setCprForm({...cprForm, selectedTime: slot})}
                                      className={`p-2 rounded-lg text-sm transition-all ${cprForm.selectedTime === slot ? 'bg-[#c31c31] text-white' : 'bg-white hover:bg-[#c31c31]/10 text-[#2a2873]'}`}>
                                      {slot}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-[#5a2b8a]/60 text-sm">Closed - By appointment only</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Step 4: Contact Info */}
                    {cprForm.selectedTime && (
                      <div className="space-y-4 pt-4 border-t">
                        <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#c31c31] text-white text-sm flex items-center justify-center">4</span> Your Information</Label>
                        
                        {/* K-12 Group Course - Special Fields */}
                        {isK12GroupCourse ? (
                          <>
                            <div className="bg-gradient-to-r from-red-50 to-purple-50 p-4 rounded-xl border border-purple-200 mb-4">
                              <p className="font-semibold text-[#2a2873] flex items-center gap-2 mb-1">
                                <Heart className="w-4 h-4 text-[#c31c31]" /> K-12 Student Registration
                              </p>
                              <p className="text-sm text-[#5a2b8a]/70">Register one or more children for this course</p>
                            </div>
                            
                            {/* Parent/Guardian Info */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label>Parent/Guardian Name *</Label>
                                <Input required value={parentName} onChange={e => setParentName(e.target.value)} placeholder="Parent or guardian full name" />
                              </div>
                              <div><Label>Email *</Label><Input type="email" required value={cprForm.email} onChange={e => setCprForm({...cprForm, email: e.target.value})} /></div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label>Phone *</Label><Input type="tel" required value={cprForm.phone} onChange={e => setCprForm({...cprForm, phone: e.target.value})} /></div>
                              <div><Label>Address</Label><Input value={cprForm.address} onChange={e => setCprForm({...cprForm, address: e.target.value})} /></div>
                            </div>
                            
                            {/* Children Names */}
                            <div className="space-y-3">
                              <Label className="font-semibold text-[#2a2873]">Child(ren) to Register *</Label>
                              {k12Children.map((child, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-[#c31c31] text-white text-sm flex items-center justify-center flex-shrink-0">
                                    {index + 1}
                                  </div>
                                  <Input 
                                    required
                                    value={child.name} 
                                    onChange={e => updateChildName(index, e.target.value)} 
                                    placeholder={`Child ${index + 1} full name`}
                                    className="flex-1"
                                  />
                                  {k12Children.length > 1 && (
                                    <Button type="button" variant="ghost" size="sm" onClick={() => removeChild(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button type="button" variant="outline" onClick={addChild} className="w-full border-dashed border-[#5a2b8a] text-[#5a2b8a] hover:bg-purple-50">
                                <Plus className="w-4 h-4 mr-2" /> Add Another Child
                              </Button>
                              <p className="text-xs text-slate-500">Price: $40 per child • Total: ${k12Children.length * 40}</p>
                            </div>
                          </>
                        ) : isSkillsTest ? (
                          /* Skills Test - Multiple Students Registration */
                          <>
                            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-xl border border-orange-200 mb-4" data-testid="skills-test-header">
                              <p className="font-semibold text-orange-800 flex items-center gap-2">
                                <Users className="w-4 h-4" /> Skills Test Multi-Student Registration
                              </p>
                              <p className="text-sm text-orange-700">Register multiple students - each needs their own contact info</p>
                            </div>
                            
                            {/* Students List */}
                            <div className="space-y-4" data-testid="skills-test-students-container">
                              <Label className="font-semibold text-[#2a2873]">Students to Register *</Label>
                              {skillsTestStudents.map((student, index) => (
                                <div key={index} className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid={`student-card-${index}`}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] text-white text-sm flex items-center justify-center font-bold">
                                        {index + 1}
                                      </div>
                                      <span className="font-medium text-[#2a2873]">Student {index + 1}</span>
                                    </div>
                                    {skillsTestStudents.length > 1 && (
                                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSkillsTestStudent(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-student-${index}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                  <div className="grid md:grid-cols-3 gap-3">
                                    <div>
                                      <Label className="text-sm">Name *</Label>
                                      <Input 
                                        required
                                        value={student.name} 
                                        onChange={e => updateSkillsTestStudent(index, 'name', e.target.value)} 
                                        placeholder="Full name"
                                        data-testid={`student-name-${index}`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-sm">Phone *</Label>
                                      <Input 
                                        type="tel"
                                        required
                                        value={student.phone} 
                                        onChange={e => updateSkillsTestStudent(index, 'phone', e.target.value)} 
                                        placeholder="Phone number"
                                        data-testid={`student-phone-${index}`}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-sm">Email *</Label>
                                      <Input 
                                        type="email"
                                        required
                                        value={student.email} 
                                        onChange={e => updateSkillsTestStudent(index, 'email', e.target.value)} 
                                        placeholder="Email address"
                                        data-testid={`student-email-${index}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <Button type="button" variant="outline" onClick={addSkillsTestStudent} className="w-full border-dashed border-[#5a2b8a] text-[#5a2b8a] hover:bg-purple-50" data-testid="add-student-btn">
                                <Plus className="w-4 h-4 mr-2" /> Add Another Student
                              </Button>
                              <p className="text-xs text-slate-500" data-testid="skills-test-total">
                                Price: {selectedCourse?.price} per student • Total: ${extractPrice(selectedCourse?.price, 1) * skillsTestStudents.length}
                              </p>
                            </div>
                            
                            {/* Location */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label>Location *</Label>
                                <Select value={cprForm.locationType} onValueChange={v => setCprForm({...cprForm, locationType: v})}>
                                  <SelectTrigger data-testid="skills-test-location"><SelectValue placeholder="Select" /></SelectTrigger>
                                  <SelectContent>{locationTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div><Label>Address</Label><Input value={cprForm.address} onChange={e => setCprForm({...cprForm, address: e.target.value})} data-testid="skills-test-address" /></div>
                            </div>
                          </>
                        ) : (
                          /* Standard Contact Fields */
                          <>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label>Name *</Label><Input required value={cprForm.name} onChange={e => setCprForm({...cprForm, name: e.target.value})} /></div>
                              <div><Label>Email *</Label><Input type="email" required value={cprForm.email} onChange={e => setCprForm({...cprForm, email: e.target.value})} /></div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label>Phone *</Label><Input type="tel" required value={cprForm.phone} onChange={e => setCprForm({...cprForm, phone: e.target.value})} /></div>
                              <div>
                                <Label># Students *</Label>
                                <Input type="number" min={cprForm.trainingType === 'group' ? 4 : 1} required value={cprForm.numberOfStudents} onChange={e => setCprForm({...cprForm, numberOfStudents: e.target.value})} />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <Label>Location *</Label>
                                <Select value={cprForm.locationType} onValueChange={v => setCprForm({...cprForm, locationType: v})}>
                                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                                  <SelectContent>{locationTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div><Label>Address</Label><Input value={cprForm.address} onChange={e => setCprForm({...cprForm, address: e.target.value})} /></div>
                            </div>
                          </>
                        )}
                        
                        {/* Common fields for all */}
                        {!isK12GroupCourse && (
                          <div><Label>Notes</Label><Textarea rows={2} value={cprForm.notes} onChange={e => setCprForm({...cprForm, notes: e.target.value})} /></div>
                        )}
                        {isK12GroupCourse && (
                          <div>
                            <Label>Location *</Label>
                            <Select value={cprForm.locationType} onValueChange={v => setCprForm({...cprForm, locationType: v})}>
                              <SelectTrigger><SelectValue placeholder="Select location" /></SelectTrigger>
                              <SelectContent>{locationTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {isK12GroupCourse && (
                          <div><Label>Additional Notes</Label><Textarea rows={2} value={cprForm.notes} onChange={e => setCprForm({...cprForm, notes: e.target.value})} placeholder="Any allergies, special needs, or additional information" /></div>
                        )}
                        
                        {/* Document Upload */}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload Document (Optional)</Label>
                          <p className="text-xs text-slate-500">Upload ID or any relevant documents (PDF, JPG, PNG - Max 10MB)</p>
                          <input
                            type="file"
                            ref={cprFileInputRef}
                            onChange={handleCprFileChange}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            className="hidden"
                          />
                          {cprFile ? (
                            <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <File className="w-5 h-5 text-green-600" />
                              <span className="text-sm text-green-700 flex-1 truncate">{cprFile.name}</span>
                              <button type="button" onClick={() => setCprFile(null)} className="text-red-500 hover:text-red-700">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <Button type="button" variant="outline" onClick={() => cprFileInputRef.current?.click()} className="w-full border-dashed">
                              <Upload className="w-4 h-4 mr-2" /> Choose File
                            </Button>
                          )}
                        </div>
                        
                        {/* Summary */}
                        <div className="bg-gradient-to-r from-[#c31c31]/10 to-[#5a2b8a]/10 p-4 rounded-xl">
                          <div className="flex justify-between items-start flex-wrap gap-4">
                            <div>
                              <p className="text-sm text-[#5a2b8a]">{getTrainingLabel(cprForm.trainingType)}</p>
                              <p className="font-semibold text-[#2a2873]">{cprForm.courseType}</p>
                              <p className="text-sm text-[#5a2b8a] mt-1 flex items-center gap-1"><Calendar className="w-4 h-4" /> {cprForm.selectedDate?.toLocaleDateString()} at {cprForm.selectedTime}</p>
                            </div>
                            <div className="text-right">
                              {showPromo && selectedCourse?.promo ? (
                                <>
                                  <p className="text-sm text-slate-400 line-through">{selectedCourse.price}</p>
                                  <p className="font-bold text-2xl text-[#c31c31]">{selectedCourse.promo}</p>
                                  <Badge className="bg-[#c31c31] text-white">$15 OFF</Badge>
                                </>
                              ) : (
                                <p className="font-bold text-2xl text-[#c31c31]">{selectedCourse?.price}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* MERCH UPSELL */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                          <div className="flex items-center gap-2 mb-3">
                            <Shirt className="w-5 h-5 text-[#c31c31]" />
                            <h4 className="font-bold text-[#2a2873]">Add LifeBreath Merch!</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                              <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png" alt="T-Shirts" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                              <p className="font-bold text-[#c31c31] text-sm">T-Shirts $20-$30</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                              <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG" alt="Cups" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                              <p className="font-bold text-orange-500 text-sm">Cups $25</p>
                            </div>
                          </div>
                          <Link to="/shop"><Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm py-2"><ShoppingCart className="w-4 h-4 mr-2" /> Browse Merch</Button></Link>
                        </div>
                        
                        <Button type="submit" size="lg" className="w-full rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a]" disabled={isSubmittingCpr || uploadingCpr}>
                          {uploadingCpr ? 'Uploading document...' : isSubmittingCpr ? 'Submitting...' : 'Confirm Booking'} <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notary Booking for Statesboro */}
            <TabsContent value="notary-ga">
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-[#4f62c8]/5 to-[#2a2873]/5">
                  <CardTitle className="text-2xl text-[#2a2873]">Mobile Notary Appointment</CardTitle>
                  <CardDescription>Select date, time, and fill out your details</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleNotarySubmit} className="space-y-6">
                    {/* Date & Time Selection */}
                    <div className="space-y-3">
                      <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#4f62c8] text-white text-sm flex items-center justify-center">1</span> Select Date & Time</Label>
                      <div className="grid md:grid-cols-2 gap-4">
                        <BookingCalendar selectedDate={notaryForm.selectedDate} onDateSelect={(date) => setNotaryForm({...notaryForm, selectedDate: date, selectedTime: ''})} serviceType="notary" />
                        {notaryForm.selectedDate && (
                          <div className="bg-[#4f62c8]/5 rounded-xl p-4">
                            <p className="font-medium text-[#2a2873] mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Available Times</p>
                            {notaryTimeSlots.length > 0 ? (
                              <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                                {notaryTimeSlots.map(slot => (
                                  <button key={slot} type="button" onClick={() => setNotaryForm({...notaryForm, selectedTime: slot})}
                                    className={`p-2 rounded-lg text-sm transition-all ${notaryForm.selectedTime === slot ? 'bg-[#4f62c8] text-white' : 'bg-white hover:bg-[#4f62c8]/10 text-[#2a2873]'}`}>
                                    {slot}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[#5a2b8a]/60 text-sm">Closed - By appointment only</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Info */}
                    {notaryForm.selectedTime && (
                      <div className="space-y-4 pt-4 border-t">
                        <Label className="font-semibold text-[#2a2873] flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#2a2873] text-white text-sm flex items-center justify-center">2</span> Your Information</Label>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div><Label>Name *</Label><Input required value={notaryForm.name} onChange={e => setNotaryForm({...notaryForm, name: e.target.value})} /></div>
                          <div><Label>Email *</Label><Input type="email" required value={notaryForm.email} onChange={e => setNotaryForm({...notaryForm, email: e.target.value})} /></div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div><Label>Phone *</Label><Input type="tel" required value={notaryForm.phone} onChange={e => setNotaryForm({...notaryForm, phone: e.target.value})} /></div>
                          <div><Label>Address *</Label><Input required value={notaryForm.address} onChange={e => setNotaryForm({...notaryForm, address: e.target.value})} placeholder="Where should we come?" /></div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div><Label># Signers *</Label><Input type="number" min="1" required value={notaryForm.numberOfSigners} onChange={e => setNotaryForm({...notaryForm, numberOfSigners: e.target.value})} /></div>
                          <div><Label># Documents *</Label><Input type="number" min="1" required value={notaryForm.numberOfDocuments} onChange={e => setNotaryForm({...notaryForm, numberOfDocuments: e.target.value})} /></div>
                        </div>
                        <div><Label>Document Type</Label><Input value={notaryForm.documentType} onChange={e => setNotaryForm({...notaryForm, documentType: e.target.value})} placeholder="e.g., Power of Attorney, Affidavit" /></div>
                        <div><Label>Notes</Label><Textarea rows={2} value={notaryForm.notes} onChange={e => setNotaryForm({...notaryForm, notes: e.target.value})} /></div>
                        
                        {/* Summary */}
                        <div className="bg-gradient-to-r from-[#4f62c8]/10 to-[#2a2873]/10 p-4 rounded-xl">
                          <p className="font-semibold text-[#2a2873]">Mobile Notary Appointment</p>
                          <p className="text-sm text-[#5a2b8a] flex items-center gap-1"><Calendar className="w-4 h-4" /> {notaryForm.selectedDate?.toLocaleDateString()} at {notaryForm.selectedTime}</p>
                          <p className="text-sm text-[#5a2b8a] flex items-center gap-1"><MapPin className="w-4 h-4" /> {notaryForm.address || 'Address to be confirmed'}</p>
                        </div>
                        
                        {/* MERCH UPSELL */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                          <div className="flex items-center gap-2 mb-3">
                            <Shirt className="w-5 h-5 text-[#c31c31]" />
                            <h4 className="font-bold text-[#2a2873]">Add LifeBreath Merch!</h4>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                              <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png" alt="T-Shirts" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                              <p className="font-bold text-[#c31c31] text-sm">T-Shirts $20-$30</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                              <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG" alt="Cups" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                              <p className="font-bold text-orange-500 text-sm">Cups $25</p>
                            </div>
                          </div>
                          <Link to="/shop"><Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm py-2"><ShoppingCart className="w-4 h-4 mr-2" /> Browse Merch</Button></Link>
                        </div>
                        
                        <Button type="submit" size="lg" className="w-full rounded-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873]" disabled={isSubmittingNotary}>
                          {isSubmittingNotary ? 'Submitting...' : 'Confirm Appointment'} <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
              </Tabs>
            </TabsContent>

            {/* JACKSONVILLE, FL - CPR & Notary */}
            <TabsContent value="jacksonville">
              <Card className="border-0 shadow-xl mb-4">
                <CardHeader className="bg-gradient-to-r from-[#4f62c8]/10 to-[#2a2873]/10 border-b">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-[#4f62c8]" />
                    <div>
                      <CardTitle className="text-[#2a2873]">Jacksonville, Florida & Surrounding Areas</CardTitle>
                      <CardDescription>CPR Training & Mobile Notary Services • <a href="tel:904-465-5065" className="text-[#4f62c8] hover:underline">904-465-5065</a></CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              <Tabs defaultValue="cpr-fl" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 h-14">
                  <TabsTrigger value="cpr-fl" className="flex items-center gap-2 data-[state=active]:bg-[#c31c31] data-[state=active]:text-white rounded-l-full h-full" data-testid="cpr-fl-tab">
                    <Heart className="w-5 h-5" /> CPR & First Aid
                  </TabsTrigger>
                  <TabsTrigger value="notary-fl" className="flex items-center gap-2 data-[state=active]:bg-[#4f62c8] data-[state=active]:text-white rounded-r-full h-full" data-testid="notary-fl-tab">
                    <FileCheck className="w-5 h-5" /> Mobile Notary
                  </TabsTrigger>
                </TabsList>

                {/* CPR Booking for Jacksonville */}
                <TabsContent value="cpr-fl">
                  <Card className="border-0 shadow-xl">
                    <CardHeader className="bg-gradient-to-r from-[#c31c31]/5 to-[#5a2b8a]/5">
                      <CardTitle className="text-2xl text-[#2a2873]">CPR/First Aid Training - Jacksonville</CardTitle>
                      <CardDescription>Professional training available by appointment</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="text-center py-8">
                        <Heart className="w-16 h-16 mx-auto text-[#c31c31]/30 mb-4" />
                        <h3 className="text-xl font-semibold text-[#2a2873] mb-2">Contact Us to Schedule</h3>
                        <p className="text-[#5a2b8a]/70 mb-6 max-w-md mx-auto">
                          For CPR & First Aid training in the Jacksonville area, please contact us directly to arrange your session.
                        </p>
                        <a href="tel:904-465-5065">
                          <Button size="lg" className="rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a]">
                            <Phone className="mr-2 w-5 h-5" /> Call 904-465-5065
                          </Button>
                        </a>
                        <p className="text-sm text-[#5a2b8a]/60 mt-4">Shiffon Champion-Holmes</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Notary Booking for Jacksonville */}
                <TabsContent value="notary-fl">
                  <Card className="border-0 shadow-xl">
                    <CardHeader className="bg-gradient-to-r from-[#4f62c8]/5 to-[#2a2873]/5">
                      <CardTitle className="text-2xl text-[#2a2873]">Mobile Notary - Jacksonville, FL</CardTitle>
                      <CardDescription>Shiffon Champion-Holmes • 904-465-5065</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                      {/* Jacksonville Pricing Info */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {/* Notary Fees */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
                          <h4 className="font-bold text-[#2a2873] mb-3 flex items-center gap-2">
                            <FileCheck className="w-5 h-5 text-[#4f62c8]" /> Notary Fees
                          </h4>
                          <p className="text-sm text-[#5a2b8a]">Per signature: <span className="font-bold text-[#2a2873]">$10</span></p>
                          <p className="text-xs text-slate-500 mt-1">(Florida state-regulated)</p>
                        </div>
                        
                        {/* Travel Fees */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
                          <h4 className="font-bold text-[#2a2873] mb-3 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#4f62c8]" /> Travel Fees
                          </h4>
                          <div className="space-y-1 text-sm text-[#5a2b8a]">
                            <p>0-10 miles: <span className="font-bold">$30</span></p>
                            <p>11-20 miles: <span className="font-bold">$45</span></p>
                            <p>21-30 miles: <span className="font-bold">$60</span></p>
                            <p>30+ miles: <span className="font-bold">Call</span></p>
                          </div>
                        </div>
                        
                        {/* Popular Services */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
                          <h4 className="font-bold text-[#2a2873] mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-[#4f62c8]" /> Popular Services
                          </h4>
                          <div className="space-y-1 text-sm text-[#5a2b8a]">
                            <p>Single Signature: <span className="font-bold">$40-$55</span></p>
                            <p>2-5 Signatures: <span className="font-bold">$50-$75</span></p>
                            <p>Real Estate/Loan: <span className="font-bold">$100-$150</span></p>
                            <p>Hospital Visits: <span className="font-bold">$75+</span></p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Convenience Fees Notice */}
                      <div className="flex flex-wrap gap-3 mb-6 justify-center">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Same-Day/Rush: +$25</Badge>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">After-Hours/Weekends: +$25</Badge>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Cash | Square Accepted</Badge>
                      </div>

                      <form onSubmit={handleJaxNotarySubmit} className="space-y-6">
                        {/* Date & Time Selection */}
                        <div className="space-y-3">
                          <Label className="font-semibold text-[#2a2873] flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-[#4f62c8] text-white text-sm flex items-center justify-center">1</span> Select Date & Time
                          </Label>
                          <div className="grid md:grid-cols-2 gap-4">
                            <BookingCalendar 
                              selectedDate={jaxNotaryForm.selectedDate} 
                              onDateSelect={(date) => setJaxNotaryForm({...jaxNotaryForm, selectedDate: date, selectedTime: ''})} 
                              serviceType="notary" 
                            />
                            {jaxNotaryForm.selectedDate && (
                              <div className="bg-white rounded-xl border p-4">
                                <div className="flex items-center gap-2 text-[#2a2873] mb-3">
                                  <Clock className="w-5 h-5 text-[#4f62c8]" />
                                  <span className="font-medium">{jaxNotaryForm.selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                                </div>
                                {jaxNotaryTimeSlots.length > 0 ? (
                                  <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
                                    {jaxNotaryTimeSlots.map(slot => (
                                      <button key={slot} type="button" onClick={() => setJaxNotaryForm({...jaxNotaryForm, selectedTime: slot})}
                                        className={`p-2 rounded-lg text-sm transition-all ${jaxNotaryForm.selectedTime === slot ? 'bg-[#4f62c8] text-white' : 'bg-white hover:bg-[#4f62c8]/10 text-[#2a2873]'}`}>
                                        {slot}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-[#5a2b8a]/60 text-sm">Closed - By appointment only. Call 904-465-5065</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Contact Info */}
                        {jaxNotaryForm.selectedTime && (
                          <div className="space-y-4 pt-4 border-t">
                            <Label className="font-semibold text-[#2a2873] flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-[#2a2873] text-white text-sm flex items-center justify-center">2</span> Your Information
                            </Label>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label>Name *</Label><Input required value={jaxNotaryForm.name} onChange={e => setJaxNotaryForm({...jaxNotaryForm, name: e.target.value})} /></div>
                              <div><Label>Email *</Label><Input type="email" required value={jaxNotaryForm.email} onChange={e => setJaxNotaryForm({...jaxNotaryForm, email: e.target.value})} /></div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label>Phone *</Label><Input type="tel" required value={jaxNotaryForm.phone} onChange={e => setJaxNotaryForm({...jaxNotaryForm, phone: e.target.value})} /></div>
                              <div><Label>Address *</Label><Input required value={jaxNotaryForm.address} onChange={e => setJaxNotaryForm({...jaxNotaryForm, address: e.target.value})} placeholder="Where should we come?" /></div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div><Label># Signers *</Label><Input type="number" min="1" required value={jaxNotaryForm.numberOfSigners} onChange={e => setJaxNotaryForm({...jaxNotaryForm, numberOfSigners: e.target.value})} /></div>
                              <div><Label># Documents *</Label><Input type="number" min="1" required value={jaxNotaryForm.numberOfDocuments} onChange={e => setJaxNotaryForm({...jaxNotaryForm, numberOfDocuments: e.target.value})} /></div>
                            </div>
                            <div><Label>Document Type</Label><Input value={jaxNotaryForm.documentType} onChange={e => setJaxNotaryForm({...jaxNotaryForm, documentType: e.target.value})} placeholder="e.g., Power of Attorney, Affidavit" /></div>
                            <div><Label>Notes</Label><Textarea rows={2} value={jaxNotaryForm.notes} onChange={e => setJaxNotaryForm({...jaxNotaryForm, notes: e.target.value})} /></div>
                            
                            {/* Summary */}
                            <div className="bg-gradient-to-r from-[#4f62c8]/10 to-[#2a2873]/10 p-4 rounded-xl">
                              <p className="font-semibold text-[#2a2873]">Jacksonville Mobile Notary</p>
                              <p className="text-sm text-[#5a2b8a] flex items-center gap-1"><Calendar className="w-4 h-4" /> {jaxNotaryForm.selectedDate?.toLocaleDateString()} at {jaxNotaryForm.selectedTime}</p>
                              <p className="text-sm text-[#5a2b8a] flex items-center gap-1"><MapPin className="w-4 h-4" /> {jaxNotaryForm.address || 'Address to be confirmed'}</p>
                              <p className="text-sm text-[#4f62c8] flex items-center gap-1 mt-1"><Phone className="w-4 h-4" /> Shiffon Champion-Holmes</p>
                            </div>
                            
                            {/* MERCH UPSELL */}
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                              <div className="flex items-center gap-2 mb-3">
                                <Shirt className="w-5 h-5 text-[#c31c31]" />
                                <h4 className="font-bold text-[#2a2873]">Add LifeBreath Merch!</h4>
                              </div>
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                                  <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png" alt="T-Shirts" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                                  <p className="font-bold text-[#c31c31] text-sm">T-Shirts $20-$30</p>
                                </div>
                                <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                                  <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG" alt="Cups" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                                  <p className="font-bold text-orange-500 text-sm">Cups $25</p>
                                </div>
                              </div>
                              <Link to="/shop"><Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm py-2"><ShoppingCart className="w-4 h-4 mr-2" /> Browse Merch</Button></Link>
                            </div>
                            
                            <Button type="submit" size="lg" className="w-full rounded-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873]" disabled={isSubmittingJaxNotary}>
                              {isSubmittingJaxNotary ? 'Submitting...' : 'Confirm Jacksonville Appointment'} <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                          </div>
                        )}
                      </form>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>

          {/* Payment */}
          <Card className="mt-12 border-0 shadow-xl bg-gradient-to-r from-red-50 to-purple-50">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-[#2a2873] mb-4 text-center">Ready to Pay?</h3>
              <p className="text-[#5a2b8a]/70 mb-6 text-center">After booking confirmation, use our secure payment.</p>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                <img src="https://customer-assets.emergentagent.com/job_quickcert-ga/artifacts/wjw1qen5_checkout-link-qr-code%20%284%29.png" alt="QR Code" className="w-32 h-32 rounded-lg shadow-md" />
                <a href="https://square.link/u/M4UFv2fT" target="_blank" rel="noopener noreferrer">
                  <Button className="rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a]">
                    <CheckCircle className="mr-2 w-5 h-5" /> Pay with Square
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Payment Modal */}
      {showPaymentModal && pendingBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="payment-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] p-6 text-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Complete Your Booking
                </h3>
                <button 
                  onClick={() => setShowPaymentModal(false)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-white/80 text-sm mt-2">Booking confirmed! Secure your spot with payment.</p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600">Course</p>
                <p className="font-semibold text-[#2a2873]">{pendingBooking.description}</p>
              </div>
              
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600">Customer</p>
                <p className="font-semibold text-[#2a2873]">{pendingBooking.customer_name}</p>
                <p className="text-sm text-slate-500">{pendingBooking.customer_email}</p>
              </div>

              <div className="bg-gradient-to-r from-[#c31c31]/10 to-[#5a2b8a]/10 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600">Total Amount</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] bg-clip-text text-transparent">
                  ${pendingBooking.amount.toFixed(2)}
                </p>
              </div>

              {/* MERCH UPSELL - Inside Registration */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                <div className="flex items-center gap-2 mb-3">
                  <Shirt className="w-5 h-5 text-[#c31c31]" />
                  <h4 className="font-bold text-[#2a2873]">Add LifeBreath Merch to Your Order!</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png"
                      alt="T-Shirts"
                      className="w-16 h-16 object-cover rounded-lg mx-auto mb-1"
                    />
                    <p className="font-bold text-[#c31c31] text-sm">T-Shirts</p>
                    <p className="text-xs text-slate-500">$20-$30</p>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG"
                      alt="Specialty Cups"
                      className="w-16 h-16 object-cover rounded-lg mx-auto mb-1"
                    />
                    <p className="font-bold text-orange-500 text-sm">Cups</p>
                    <p className="text-xs text-slate-500">$25</p>
                  </div>
                </div>
                <Link to="/shop" onClick={() => setShowPaymentModal(false)}>
                  <Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white text-sm py-2">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Browse Merch Shop
                  </Button>
                </Link>
              </div>

              {/* Actions */}
              <div className="space-y-3 pt-2">
                <Button 
                  onClick={handlePayNow}
                  disabled={paymentLoading}
                  className="w-full rounded-full bg-gradient-to-r from-[#c31c31] to-[#5a2b8a] hover:from-[#a51828] hover:to-[#4a2370] py-6 text-lg"
                  data-testid="pay-now-btn"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Creating payment link...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Now with Square
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={handlePayLater}
                  variant="outline"
                  className="w-full rounded-full border-2 border-slate-200 hover:bg-slate-50 py-6"
                  data-testid="pay-later-btn"
                >
                  Pay Later (We'll contact you)
                </Button>
              </div>

              <p className="text-xs text-center text-slate-400">
                Secure payment powered by Square. Your booking is confirmed either way.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
